import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  App,
  Button,
  Card,
  Col,
  Divider,
  Form,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from "antd";
import { useAuth } from "../../../context/AuthContext";
import { useThemeStyles } from "../../../context/ThemeContext";
import {
  getAdminCostSummary,
  getAdminProjectBudget,
  getAdminProjectAllocationSummary,
  getAdminProjectUserBudgets,
  listUsers,
  upsertAdminUserBudget,
} from "../../../services/api";
import PageHeader from "../../../components/PageHeader";
import { pageShellStyle } from "../../../theme";
import { formatUsd } from "../utils";

const { Text } = Typography;

export default function AdminUserBudgetsPage() {
  const { token, isAdmin } = useAuth();
  const themeStyles = useThemeStyles();
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(null);
  const [userBudgets, setUserBudgets] = useState([]);
  const [assignedUserIds, setAssignedUserIds] = useState([]);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [userDirectory, setUserDirectory] = useState({});
  const [allocation, setAllocation] = useState(null);
  const [allUsers, setAllUsers] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [activeUserId, setActiveUserId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const loadProjects = useCallback(async () => {
    if (!token || !isAdmin) return;
    setLoading(true);
    try {
      const res = await getAdminCostSummary(token, 30);
      const list = Array.isArray(res?.data?.projects) ? res.data.projects : [];
      setProjects(list);
      if (!projectId && list[0]?.project_id) setProjectId(list[0].project_id);
    } catch (e) {
      message.error(e?.response?.data?.detail || "Failed to load projects");
    } finally {
      setLoading(false);
    }
  }, [token, isAdmin, projectId, message]);

  const loadUsers = useCallback(async () => {
    if (!token) return;
    try {
      const res = await listUsers(token);
      setAllUsers(Array.isArray(res?.data) ? res.data : []);
    } catch {
      setAllUsers([]);
    }
  }, [token]);

  const loadProject = useCallback(async () => {
    if (!token || !isAdmin || !projectId) return;
    setLoading(true);
    try {
      const [budgetsRes, allocRes, projBudgetRes] = await Promise.all([
        getAdminProjectUserBudgets(token, projectId),
        getAdminProjectAllocationSummary(token, projectId),
        getAdminProjectBudget(token, projectId, 30),
      ]);
      setUserBudgets(Array.isArray(budgetsRes?.data?.user_budgets) ? budgetsRes.data.user_budgets : []);
      setAssignedUserIds(Array.isArray(budgetsRes?.data?.assigned_user_ids) ? budgetsRes.data.assigned_user_ids : []);
      setAssignedUsers(Array.isArray(budgetsRes?.data?.assigned_users) ? budgetsRes.data.assigned_users : []);
      setUserDirectory(budgetsRes?.data?.user_directory && typeof budgetsRes.data.user_directory === "object" ? budgetsRes.data.user_directory : {});
      const alloc = allocRes?.data || null;
      const projectBudgetUsd = projBudgetRes?.data?.budget?.monthly_limit_usd;
      if (alloc && (alloc.project_budget_usd === null || alloc.project_budget_usd === undefined) && projectBudgetUsd !== undefined) {
        alloc.project_budget_usd = projectBudgetUsd;
        if (projectBudgetUsd !== null && Number(projectBudgetUsd) > 0) {
          const remaining = Math.max(0, Number(projectBudgetUsd) - Number(alloc.total_allocated_usd || 0));
          alloc.remaining_unallocated_usd = remaining;
        }
      }
      setAllocation(alloc);
    } catch (e) {
      message.error(e?.response?.data?.detail || "Failed to load user budgets");
    } finally {
      setLoading(false);
    }
  }, [token, isAdmin, projectId, message]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  const openEdit = (userId, existing) => {
    setActiveUserId(userId);
    form.setFieldsValue({
      allocated_budget_usd:
        existing?.allocated_budget_usd === null || existing?.allocated_budget_usd === undefined
          ? null
          : Number(existing.allocated_budget_usd),
      enabled: existing?.enabled ?? true,
      block_when_exceeded: existing?.block_when_exceeded ?? false,
      alert_thresholds: Array.isArray(existing?.alert_thresholds) ? existing.alert_thresholds : [80, 90],
    });
    setModalOpen(true);
  };

  const submit = async () => {
    const v = await form.validateFields();
    if (!token || !projectId || !activeUserId) return;
    setSubmitting(true);
    try {
      await upsertAdminUserBudget(token, projectId, activeUserId, {
        allocated_budget_usd: v.allocated_budget_usd === null || v.allocated_budget_usd === undefined ? null : Number(v.allocated_budget_usd),
        enabled: !!v.enabled,
        block_when_exceeded: !!v.block_when_exceeded,
        alert_thresholds: (v.alert_thresholds || [80, 90]).map((x) => Number(x)).filter((x) => x >= 1 && x <= 100),
      });
      message.success("User budget updated");
      setModalOpen(false);
      loadProject();
    } catch (e) {
      message.error(e?.response?.data?.detail || "Failed to update user budget");
    } finally {
      setSubmitting(false);
    }
  };

  const usersById = useMemo(() => {
    const map = {};
    for (const u of allUsers || []) {
      const id = String(u?._id || "");
      if (!id) continue;
      map[id] = String(u?.username || "");
    }
    return map;
  }, [allUsers]);

  const columns = useMemo(
    () => [
      {
        title: "User",
        dataIndex: "user_id",
        key: "user_id",
        width: 260,
        ellipsis: true,
        render: (uid, row) => {
          const id = String(uid || "");
          const name =
            row?.username
            || assignedUsers.find((u) => String(u?.user_id || "") === id)?.username
            || usersById?.[id]
            || userDirectory?.[id]
            || "";
          return name || id;
        },
      },
      {
        title: "Allocated",
        dataIndex: "allocated_budget_usd",
        key: "allocated_budget_usd",
        width: 120,
        render: (v) => (v == null ? "—" : formatUsd(v)),
      },
      {
        title: "Enabled",
        dataIndex: "enabled",
        key: "enabled",
        width: 90,
        render: (v) => (v ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>),
      },
      {
        title: "Blocking",
        dataIndex: "block_when_exceeded",
        key: "block_when_exceeded",
        width: 100,
        render: (v) => (v ? <Tag color="red">On</Tag> : <Tag color="gold">Off</Tag>),
      },
      {
        title: "Thresholds",
        dataIndex: "alert_thresholds",
        key: "alert_thresholds",
        width: 120,
        render: (v) => (Array.isArray(v) && v.length ? v.join(", ") : "—"),
      },
      {
        title: "Action",
        key: "action",
        width: 110,
        render: (_, row) => (
          <Button size="small" onClick={() => openEdit(row.user_id, row)}>
            Edit
          </Button>
        ),
      },
    ],
    [userDirectory, assignedUsers, usersById],
  );

  if (!isAdmin) {
    return (
      <div style={{ ...pageShellStyle, padding: 18 }}>
        <Card>
          <Text type="secondary">Admin access required.</Text>
        </Card>
      </div>
    );
  }

  const projectOptions = projects.map((p) => ({ value: p.project_id, label: p.name || p.project_id }));

  const assignedOptions = (assignedUsers.length ? assignedUsers.map((u) => u?.user_id) : assignedUserIds).map((uid) => {
    const id = String(uid || "");
    const name =
      assignedUsers.find((u) => String(u?.user_id || "") === id)?.username
      || usersById?.[id]
      || userDirectory?.[id];
    return { value: id, label: name || id };
  });
  const existingUserIds = new Set((userBudgets || []).map((r) => String(r.user_id)));
  const unconfiguredUserOptions = assignedOptions.filter((o) => !existingUserIds.has(String(o.value)));

  return (
    <div style={{ ...pageShellStyle, padding: 18 }}>
      <PageHeader title="User budgets" subtitle="Allocate per-user budgets inside a project" />

      <Row gutter={[12, 12]}>
        <Col span={24}>
          <Card loading={loading}>
            <Space direction="vertical" style={{ width: "100%" }} size={10}>
              <Space>
                <Text strong style={{ color: themeStyles.textPrimary }}>Project</Text>
                <Select
                  value={projectId}
                  onChange={(v) => setProjectId(v)}
                  showSearch
                  options={projectOptions}
                  style={{ minWidth: 320 }}
                  placeholder="Select project"
                />
                <Button
                  type="primary"
                  disabled={!projectId || unconfiguredUserOptions.length === 0}
                  onClick={() => {
                    const first = unconfiguredUserOptions[0]?.value || null;
                    if (first) openEdit(first, null);
                  }}
                >
                  Add user budget
                </Button>
              </Space>

              {allocation ? (
                <Space>
                  <Tag color="blue">
                    Project budget: {allocation.project_budget_usd == null ? "Not set" : formatUsd(allocation.project_budget_usd)}
                  </Tag>
                  <Tag color="purple">Allocated: {formatUsd(allocation.total_allocated_usd)}</Tag>
                  <Tag color="default">
                    Remaining: {allocation.project_budget_usd == null ? "—" : (allocation.remaining_unallocated_usd == null ? "—" : formatUsd(allocation.remaining_unallocated_usd))}
                  </Tag>
                </Space>
              ) : null}

              <Divider style={{ margin: "8px 0" }} />
              <Table
                rowKey={(r) => `${r.project_id}-${r.user_id}`}
                columns={columns}
                dataSource={userBudgets}
                pagination={{ pageSize: 10 }}
              />
              <Text type="secondary">
                Note: You can allocate budgets only to users already added to the project.
              </Text>
            </Space>
          </Card>
        </Col>
      </Row>

      <Modal
        title="Edit user budget"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={submit}
        confirmLoading={submitting}
        okText="Save"
      >
        <Form layout="vertical" form={form}>
          <Form.Item label="User">
            <Select
              value={activeUserId}
              onChange={(v) => setActiveUserId(v)}
              showSearch
              options={assignedOptions}
              placeholder="Select a user"
            />
          </Form.Item>
          <Form.Item label="Enabled" name="enabled" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item
            label="Allocated budget (USD) (null = no user cap)"
            name="allocated_budget_usd"
            extra="Supports up to 6 decimal places (e.g. 0.002 for small test caps)."
          >
            <InputNumber min={0} precision={6} step={0.001} style={{ width: "100%" }} placeholder="e.g. 50 or 0.002" />
          </Form.Item>
          <Form.Item label="Block when exceeded" name="block_when_exceeded" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label="Alert thresholds" name="alert_thresholds">
            <Select
              mode="multiple"
              options={[
                { value: 80, label: "80%" },
                { value: 90, label: "90%" },
                { value: 100, label: "100%" },
              ]}
              placeholder="Select thresholds"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

